import React, { useState, useEffect } from "react";
import api from '../api';
import { formatDateTime } from '../utils/formatDate';
import PageLayout from '../components/PageLayout';
import { useToast } from '../context/ToastContext';



const Dashboard = () => {
  const { error: toastError } = useToast();
  const [recentScans, setRecentScans] = useState([]);
  const [stats, setStats] = useState({
    totalDomains: 0,
    onlineCount: 0,
    offlineCount: 0,
    sslValidCount: 0,
    sslExpiringCount: 0,
    malwareCount: 0,
    avgScore: 0,
    secureCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const results = await Promise.allSettled([
          api.get('/api/recent-scans', { params: { limit: 3 } }),
          api.get('/api/dashboard/stats')
        ]);

        const [scansRes, statsRes] = results;

        if (scansRes.status === 'fulfilled') {
          setRecentScans(scansRes.value.data.reports || []);
        } else {
          console.error('Failed to fetch recent scans:', scansRes.reason);
        }

        if (statsRes.status === 'fulfilled') {
          setStats(statsRes.value.data);
        } else {
          console.error('Failed to fetch dashboard stats:', statsRes.reason);
        }

        if (scansRes.status !== 'fulfilled' && statsRes.status !== 'fulfilled') {
          toastError('Failed to load dashboard data');
        }

      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        if (err.response && err.response.status === 401) {
          return;
        }
        toastError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, [toastError]);

  const getGradeBadgeClass = (grade) => {
    switch (grade) {
      case 'A': return 'bg-success';
      case 'B': return 'bg-info';
      case 'C': return 'bg-warning';
      case 'D': return 'bg-warning';
      case 'F': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const handleViewReport = (downloadUrl) => {
    window.open(downloadUrl, '_blank');
  };

  return (
    <PageLayout title="Dashboard">
      <h2 className="mb-4">Website Security Dashboard</h2>

      {/* Card 1: Recent Security Reports (PDFs) */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0 card-title">
                <i className="las la-file-pdf me-2"></i>
                Recent Generated Reports
              </h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : recentScans.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p>No reports generated yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Grade</th>
                        <th>Score</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentScans.map((scan, index) => (
                        <tr key={scan.id || index}>
                          <td><strong>{scan.domain}</strong></td>
                          <td><span className={`badge ${getGradeBadgeClass(scan.security_grade)}`}>{scan.security_grade}</span></td>
                          <td>{scan.security_score}/100</td>
                          <td>
                            <small className="text-muted">
                              {formatDateTime(scan.created_at)}
                            </small>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-primary" onClick={() => handleViewReport(scan.downloadUrl)}>
                              <i className="las la-download"></i> PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-4 mb-4">
        {/* Card 2: Total Domains */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">Total Domains</h6>
                <div className="icon-circle bg-primary bg-opacity-10 text-primary p-2 rounded-circle">
                  <i className="las la-globe la-2x"></i>
                </div>
              </div>
              <h2 className="display-6 fw-bold mb-0">{stats.totalDomains}</h2>
              <p className="text-muted small mt-2 mb-0">Monitored Websites</p>
            </div>
          </div>
        </div>

        {/* Card 3: Online Status */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">System Status</h6>
                <div className="icon-circle bg-success bg-opacity-10 text-success p-2 rounded-circle">
                  <i className="las la-server la-2x"></i>
                </div>
              </div>
              <div className="d-flex justify-content-between">
                <div>
                  <h3 className="fw-bold text-success mb-0">{stats.onlineCount}</h3>
                  <small className="text-muted">Online</small>
                </div>
                <div className="text-end">
                  <h3 className="fw-bold text-danger mb-0">{stats.offlineCount}</h3>
                  <small className="text-muted">Offline</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Average Security Score */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-info">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">Avg Security Score</h6>
                <div className="icon-circle bg-info bg-opacity-10 text-info p-2 rounded-circle">
                  <i className="las la-shield-alt la-2x"></i>
                </div>
              </div>
              <h2 className="display-6 fw-bold mb-0">{stats.avgScore}</h2>
              <div className="progress mt-2 h-[6px]">
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${stats.avgScore}%` }}
                  aria-valuenow={stats.avgScore}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 5: SSL Health */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">SSL Health</h6>
                <div className="icon-circle bg-warning bg-opacity-10 text-warning p-2 rounded-circle">
                  <i className="las la-lock la-2x"></i>
                </div>
              </div>
              <div className="d-flex justify-content-between">
                <div>
                  <h3 className="fw-bold text-success mb-0">{stats.sslValidCount}</h3>
                  <small className="text-muted">Valid</small>
                </div>
                <div className="text-end">
                  <h3 className="fw-bold text-warning mb-0">{stats.sslExpiringCount}</h3>
                  <small className="text-muted">Expiring Soon</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 6: Malware Risks */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-danger">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">Malware Risks</h6>
                <div className="icon-circle bg-danger bg-opacity-10 text-danger p-2 rounded-circle">
                  <i className="las la-bug la-2x"></i>
                </div>
              </div>
              <h2 className="display-6 fw-bold mb-0">{stats.malwareCount}</h2>
              <p className="text-muted small mt-2 mb-0">Domains with critical issues</p>
            </div>
          </div>
        </div>

        {/* Card 7: Secure Domains */}
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm h-100 border-start border-4 border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">Secure Domains</h6>
                <div className="icon-circle bg-success bg-opacity-10 text-success p-2 rounded-circle">
                  <i className="las la-check-circle la-2x"></i>
                </div>
              </div>
              <h2 className="display-6 fw-bold mb-0">{stats.secureCount}</h2>
              <p className="text-muted small mt-2 mb-0">Domains with Grade A</p>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Dashboard;

